/**
 * The CHROME of flow C · অপ্রাপ্য এআই — and nothing that varies per conversation.
 *
 * Every Bangla string below is copied from `Oprappo P0 Screens.dc.html`, frames C1–C5.
 * Nothing is invented — docs/01 § prohibited: "Every Bangla string in the design is
 * final. Missing strings: ask."
 *
 * ── WHAT THIS FILE USED TO HOLD, AND WHY IT NO LONGER DOES ───────────────────
 * It held the ongoing conversation, the thread, the bookable card, the three compared
 * technicians, the seven plan lines and the budget — as module constants. So every
 * conversation rendered রহিম ইলেকট্রিক at ৪.৭ ★ · ১.২ কিমি, whatever had been asked.
 * That is the same defect `routes/search/client.ts § SearchResultItem` records for the
 * search door, and the fix is the same one: the screen renders what the server sent, and
 * a field the server did not send is a line that does not appear.
 *
 * All of it now comes from `/v1/ai/conversations/**` (`./api.ts`). The drawn values
 * themselves were not lost — they are seeded as DUMMY DATA in
 * `supabase/migrations/0062_conversation_seed.sql` and served to the visual gate from
 * `fixtures/api/ai.*.json`, which `apps/api/src/ai/__tests__/fixtures.test.ts` asserts
 * against the real route so the fixture cannot go stale.
 *
 * ── WHAT STAYS, AND WHY IT IS NOT AN INCONSISTENCY ───────────────────────────
 * A heading, a lede, an example and a placeholder are properties of the FRAME. They are
 * drawn identically whatever the list contains, they are the same on an empty screen as
 * on a full one, and fetching them would mean a screen whose own title can fail to load.
 * Flow F takes the opposite decision for `২টি` and `বাংলা` and is right to: those are
 * FACTS about the customer's data that happen to be rendered as Bangla words, and the
 * server owns facts. These are not facts.
 */

/* --------------------------------------------------------------- C1 · list --- */

export const AI_HEADER_BN = 'অপ্রাপ্য এআই';

/**
 * C1's title and lede. Audit finding 01 changed this tab's character: it is no longer
 * the door for a new request — search is the one door — it is where ongoing and past
 * requests live. The lede is the design saying so in the user's own words.
 */
export const C1_COPY = {
  title_bn: 'আপনার অনুরোধগুলো',
  lede_bn: 'সার্চে পুরো কথা লিখলে এখানেই চলে আসবেন। আগের আলোচনা এখানে জমা থাকে।',
  ongoing_heading_bn: 'চলমান',
  examples_heading_bn: 'এআই এসব সামলাতে পারে',
  composer_placeholder_bn: 'লিখুন…',
} as const;

/**
 * Four examples, each a different shape of answer. The frame note: "The last one names a
 * real constraint ('টাকা নেই'), which is how people actually ask." Three are drawn; the
 * caption counts four, and the fourth is the ongoing row, which now comes from the
 * server rather than from here.
 */
export const EXAMPLES: readonly string[] = [
  'আমার এসি নষ্ট, আজই কাউকে দরকার',
  'আগামী শুক্রবার ৫০ জনের বিয়ের আয়োজন করতে চাই',
  'গ্রাফিক ডিজাইন শিখতে চাই, টাকা নেই',
];

/* ------------------------------------------------------- C2 · conversation --- */

/**
 * C2's composer placeholder.
 *
 * Chrome, like C1's. It is drawn on the composer — a control that belongs to the screen —
 * rather than on the answer, so it does not travel with the thread and does not go blank
 * on a thread the AI has only just opened.
 */
export const C2_COPY = {
  composer_placeholder_bn: 'আরও কিছু বলুন…',
} as const;
